import {
  ID,
  TaskFilterOptions,
  TaskPriorityOptions,
  TaskType,
} from '@griply/types';

import {
  isDurationFilterActive,
  matchesDurationFilter,
} from './matches-duration-filter';

type FilterableTask = {
  id: string;
  lifeAreaId?: string | null;
  goalId?: string | null;
  completedAt?: Date | null;
  archivedAt?: Date | null;
  deletedAt?: Date | null;
  type: TaskType;
  priority?: TaskPriorityOptions | null;
  timeslot?: { duration?: number | null } | null;
  endStrategy?: { deadline?: Date | null } | null;
  deadlineDeadline?: Date | null;
  parentIds?: string[] | null;
  action?: unknown;
};

export const filterTasksClientSide = <T extends FilterableTask>(
  tasks: T[],
  filterOptions: TaskFilterOptions[],
): T[] => {
  // If no filters, return as-is
  if (!filterOptions.length) {
    return tasks;
  }

  // Deduplicate by task ID
  const uniqueDataMap = new Map<ID, T>();
  tasks.forEach((task) => {
    if (task?.id) {
      uniqueDataMap.set(task.id, task);
    }
  });
  const uniqueData = Array.from(uniqueDataMap.values());

  // Filter tasks - must match AT LEAST ONE filter set
  return uniqueData.filter((task) => {
    return filterOptions.some((filters) => {
      // Deleted-task gating mirrors web's useFilteredTasksQuery: a deleted task
      // only passes a filter set that explicitly opts into deleted, and a live
      // task only passes a filter set that does not.
      if (
        (filters.deleted && !task.deletedAt) ||
        (!filters.deleted && !!task.deletedAt)
      ) {
        return false;
      }

      // Exclude archived tasks unless explicitly opted in
      if (!filters.includeArchived && task.archivedAt) {
        return false;
      }

      // If no filter conditions are active, include the task
      if (
        !(
          filters.ids?.length ||
          filters.goals?.length ||
          filters.noGoal ||
          filters.lifeAreas?.length ||
          filters.noLifeArea ||
          isDurationFilterActive(filters) ||
          filters.priorities?.length ||
          filters.excludeIds?.length ||
          !filters.includeTaskWithAction ||
          filters.noDeadline ||
          filters.noDeadlineDeadline ||
          filters.hasDeadline ||
          filters.hasDeadlineDeadline ||
          filters.parentIds?.length ||
          (filters.noParentIds ?? true) ||
          filters.archivedGoalIds?.length
        )
      ) {
        return true;
      }

      // Check priorities
      if (filters.priorities?.length) {
        const taskPriority = task.priority ?? TaskPriorityOptions.None;
        if (!filters.priorities.includes(taskPriority)) {
          return false;
        }
      }

      // Check ids
      if (filters.ids && !filters.ids.includes(task.id)) {
        return false;
      }

      // Check noLifeArea — also exclude tasks whose life area comes from their goal
      if (filters.noLifeArea && task.lifeAreaId) {
        return false;
      }
      if (
        filters.noLifeArea &&
        task.goalId &&
        filters.lifeAreaLinkedGoalIds?.includes(task.goalId)
      ) {
        return false;
      }

      // Check noGoal
      if (filters.noGoal && task.goalId) {
        return false;
      }

      // Check noDeadline
      if (filters.noDeadline && task.endStrategy?.deadline) {
        return false;
      }

      // Check hasDeadline
      if (filters.hasDeadline && !task.endStrategy?.deadline) {
        return false;
      }

      // Check noDeadlineDeadline
      if (filters.noDeadlineDeadline && task.deadlineDeadline) {
        return false;
      }

      // Check hasDeadlineDeadline
      if (filters.hasDeadlineDeadline && !task.deadlineDeadline) {
        return false;
      }

      // Check parentIds
      if (
        filters.parentIds?.length &&
        !task.parentIds?.some((parentId) =>
          filters.parentIds!.includes(parentId),
        )
      ) {
        return false;
      }

      // Default noParentIds behavior (exclude subtasks if no parentIds filter)
      const applyNoParentIds =
        (filters.noParentIds ?? true) && !filters.parentIds?.length;
      if (applyNoParentIds && !!task.parentIds?.length) {
        return false;
      }

      // Check lifeAreas/goals combined
      const linkedItemIds =
        filters.lifeAreas?.length || filters.goals?.length
          ? [...(filters.lifeAreas ?? []), ...(filters.goals ?? [])]
          : undefined;

      if (
        linkedItemIds &&
        !(
          (task.lifeAreaId && linkedItemIds.includes(task.lifeAreaId)) ||
          (task.goalId && linkedItemIds.includes(task.goalId))
        )
      ) {
        return false;
      }

      // Check duration — regular tasks carry it on their own timeslot (they have
      // no schedules, so the base timeslot is the effective duration).
      if (!matchesDurationFilter(task.timeslot?.duration, filters)) {
        return false;
      }

      // Check excludeIds
      if (filters.excludeIds?.length && filters.excludeIds.includes(task.id)) {
        return false;
      }

      // Check archivedGoalIds
      if (
        filters.archivedGoalIds?.length &&
        task.goalId &&
        filters.archivedGoalIds.includes(task.goalId)
      ) {
        return false;
      }

      // Check includeTaskWithAction
      if (!filters.includeTaskWithAction && task.action) {
        return false;
      }

      // Check completedStartDate/completedEndDate
      if (task.completedAt) {
        if (
          filters.completedStartDate !== undefined &&
          task.completedAt < filters.completedStartDate
        ) {
          return false;
        }
        if (
          filters.completedEndDate !== undefined &&
          task.completedAt > filters.completedEndDate
        ) {
          return false;
        }
      }

      return true;
    });
  });
};
